import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ContentAction,
	type ContentApprovalDTO,
	type ContentLocalizationDTO,
	type ContentTextDTO,
	type ContentTypeDTO,
	type ContentVersionDTO,
	ContentLocalizationStatus as S,
} from '@today/types'
import type {
	ApprovalTimelineViewProps,
	ApproveDialogViewProps,
	LifecycleAction,
	LifecycleBarViewProps,
	PublishDialogViewProps,
	ReasonDialogViewProps,
} from '@today/ui-domain'
import { useState } from 'react'
import { canApprove, canPullBack, submitterOf } from '@/auth/content-capabilities'
import { contentService } from '@/services/content.service'
import { contentErrorStatus, day, type Translate } from './content-ui'
import { actionBody, lifecycleActions, publishedState, publishPreview } from './review-rules'
import { statusLabel } from './text-list-rules'
import { timelineEntries } from './track-rules'
import { contentKeys, useContentT, useContentViewer, useNow } from './useContentQueries'
import { useReviewDialogs } from './useReviewDialogs'

/** Reload everything a status action can change (design D3). */
export function useReloadAfterAction(textId: string, localizationId: string | undefined) {
	const client = useQueryClient()
	return () =>
		Promise.all([
			localizationId && client.invalidateQueries({ queryKey: contentKeys.localization(localizationId) }),
			localizationId && client.invalidateQueries({ queryKey: contentKeys.approvals(localizationId) }),
			client.invalidateQueries({ queryKey: contentKeys.versions(textId) }),
			client.invalidateQueries({ queryKey: contentKeys.text(textId) }),
			client.invalidateQueries({ queryKey: contentKeys.texts }),
			client.invalidateQueries({ queryKey: contentKeys.inbox }),
		])
}

/** Submit and publish: no reason; publish goes through the schedule/replacement confirmation. */
function useDirectActions(localization: ContentLocalizationDTO | undefined, reload: () => Promise<unknown>) {
	const { errorMessage } = useContentT()
	const [publishOpen, setPublishOpen] = useState(false)
	const act = useMutation({
		mutationFn: (action: ContentAction.SUBMIT | ContentAction.PUBLISH) =>
			contentService.act(localization?.id ?? '', actionBody(action)),
		onSuccess: async () => {
			setPublishOpen(false)
			await reload()
		},
		onError: (error) => {
			if (contentErrorStatus(error) === 409) void reload()
		},
	})
	return {
		act,
		publishOpen,
		error: act.error ? errorMessage(act.error) : null,
		openPublish: () => {
			act.reset()
			setPublishOpen(true)
		},
		closePublish: () => setPublishOpen(false),
	}
}

export interface LifecycleResult {
	bar: Omit<LifecycleBarViewProps, 'canSave' | 'onAction'> & { onAction: (action: LifecycleAction) => void }
	timeline: ApprovalTimelineViewProps
	approve: ApproveDialogViewProps
	reason: ReasonDialogViewProps
	publish: PublishDialogViewProps
}

/**
 * The lifecycle of the selected localization (TO-420 content-admin-ui
 * REQ-001/REQ-005/REQ-006/REQ-007): eligibility from the track, the review
 * dialogs, the publish confirmation and the timeline.
 */
export function useLocalizationLifecycle(args: {
	text: ContentTextDTO
	type: ContentTypeDTO | undefined
	versions: readonly ContentVersionDTO[]
	localization: ContentLocalizationDTO | undefined
	dirty: boolean
	onSave: () => void
	onNewVersion: () => void
}): LifecycleResult {
	const { text, localization } = args
	const { t } = useContentT()
	const viewer = useContentViewer()
	const now = useNow()
	const track = useQuery({
		queryKey: contentKeys.approvals(localization?.id ?? ''),
		queryFn: () => contentService.approvals(localization?.id as string),
		enabled: Boolean(localization),
	})
	const reload = useReloadAfterAction(text.id, localization?.id)
	const review = useReviewDialogs(reload)
	const direct = useDirectActions(localization, reload)
	const status = localization?.status ?? S.DRAFT
	const onAction = (action: LifecycleAction) => {
		if (!localization) return
		if (action === 'save') args.onSave()
		else if (action === 'submit') direct.act.mutate(ContentAction.SUBMIT)
		else if (action === 'publish') direct.openPublish()
		else if (action === 'approve') review.openApprove(localization.id)
		else if (action === 'newVersion') args.onNewVersion()
		else review.openReason(localization.id, action)
	}
	return {
		bar: {
			t,
			label: localization ? statusLabel(localization, now) : S.DRAFT,
			published: publishedLine(localization, now),
			actions: useActions(args, track.data),
			pending: status === S.IN_REVIEW && track.isPending,
			isBusy: direct.act.isPending,
			error: direct.publishOpen ? null : direct.error,
			onAction,
		},
		timeline: { t, entries: timelineEntries(track.data ?? [], viewer.userId) },
		approve: review.approve,
		reason: review.reason,
		publish: publishProps(t, direct, localization, args.versions, now),
	}
}

/** The buttons the viewer gets for this localization. */
function useActions(
	args: { type: ContentTypeDTO | undefined; localization: ContentLocalizationDTO | undefined; dirty: boolean },
	track: ContentApprovalDTO[] | undefined,
) {
	const viewer = useContentViewer()
	const { type, localization } = args
	const approver = Boolean(localization && type && track && canApprove(viewer, type, localization, submitterOf(track)))
	return lifecycleActions({
		status: localization?.status ?? S.DRAFT,
		write: viewer.caps.write,
		publish: viewer.caps.publish,
		requiresApproval: type?.requiresApproval ?? true,
		approver,
		pullBack: localization ? canPullBack(viewer, localization, approver) : false,
		dirty: args.dirty,
	})
}

/** The in-force / scheduled line of a published localization. */
function publishedLine(localization: ContentLocalizationDTO | undefined, now: Date) {
	if (localization?.status !== S.PUBLISHED) return null
	const state = publishedState(localization, now)
	return { scheduled: state.scheduled, from: state.from && day(state.from), to: state.to && day(state.to) }
}

function publishProps(
	t: Translate,
	direct: ReturnType<typeof useDirectActions>,
	localization: ContentLocalizationDTO | undefined,
	versions: readonly ContentVersionDTO[],
	now: Date,
): PublishDialogViewProps {
	const preview = localization
		? publishPreview(localization, localization.locale, versions, now)
		: { immediate: true, effectiveStart: now.toISOString(), replaces: null }
	return {
		t,
		isOpen: direct.publishOpen,
		immediate: preview.immediate,
		effectiveStart: day(preview.effectiveStart),
		replaces: preview.replaces,
		isBusy: direct.act.isPending,
		error: direct.publishOpen ? direct.error : null,
		onConfirm: () => direct.act.mutate(ContentAction.PUBLISH),
		onCancel: direct.closePublish,
	}
}
